'use client';

import { createClient } from '@/lib/supabase/client';

export interface ForgeGoal {
  id: string;
  userId: string;
  goalType: string;
  title: string;
  description?: string;
  targetValue?: number;
  targetUnit?: string;
  startValue?: number;
  currentValue?: number;
  targetDate?: string;
  isActive: boolean;
  isAchieved: boolean;
  achievedAt?: string;
  displayOrder: number;
  createdAt: string;
}

export interface ForgeBadge {
  id: string;
  userId: string;
  badgeType: string;
  badgeKey: string;
  title: string;
  description?: string;
  icon: string;
  goalId?: string;
  earnedAt: string;
  isSeen: boolean;
}

export interface ForgeFavouriteFood {
  id: string;
  userId: string;
  foodId: string;
  foodName: string;
  calPerServing: number;
  proteinPerServing: number;
  carbsPerServing: number;
  fatPerServing: number;
  servingSizeG: number;
  servingUnit: string;
}

function mapGoal(row: any): ForgeGoal {
  return {
    id: row.id,
    userId: row.user_id,
    goalType: row.goal_type,
    title: row.title,
    description: row.description,
    targetValue: row.target_value,
    targetUnit: row.target_unit,
    startValue: row.start_value,
    currentValue: row.current_value,
    targetDate: row.target_date,
    isActive: row.is_active,
    isAchieved: row.is_achieved,
    achievedAt: row.achieved_at,
    displayOrder: row.display_order,
    createdAt: row.created_at,
  };
}

function mapBadge(row: any): ForgeBadge {
  return {
    id: row.id,
    userId: row.user_id,
    badgeType: row.badge_type,
    badgeKey: row.badge_key,
    title: row.title,
    description: row.description,
    icon: row.icon,
    goalId: row.goal_id,
    earnedAt: row.earned_at,
    isSeen: row.is_seen,
  };
}

function mapFavourite(row: any): ForgeFavouriteFood {
  return {
    id: row.id,
    userId: row.user_id,
    foodId: row.food_id,
    foodName: row.food_name,
    calPerServing: row.cal_per_serving,
    proteinPerServing: row.protein_per_serving,
    carbsPerServing: row.carbs_per_serving,
    fatPerServing: row.fat_per_serving,
    servingSizeG: row.serving_size_g,
    servingUnit: row.serving_unit,
  };
}

export const forgeGoalsService = {
  // ── Check if goals setup is complete ──────────────────────
  async isGoalsSetupComplete(userId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('forge_user_profiles')
        .select('goals_setup_complete')
        .eq('id', userId)
        .single();
      if (error) return false;
      return data?.goals_setup_complete === true;
    } catch {
      return false;
    }
  },

  // ── Save goals and mark setup complete ────────────────────
  async saveGoalsSetup(
    userId: string,
    profileData: {
      profileName: string;
      currentWeight: number;
      targetWeight: number;
      heightCm: number;
      age: number;
      gender: string;
      activityLevel: string;
      targetCalories: number;
      targetProtein: number;
      targetCarbs: number;
      targetFat: number;
      missionStartDate: string;
    },
    goals: Array<{
      goalType: string;
      title: string;
      description?: string;
      targetValue?: number;
      targetUnit?: string;
      startValue?: number;
      targetDate?: string;
      displayOrder: number;
    }>
  ): Promise<void> {
    const supabase = createClient();

    // Update profile
    await supabase
      .from('forge_user_profiles')
      .update({
        profile_name: profileData.profileName,
        weight_kg: profileData.currentWeight,
        target_weight_kg: profileData.targetWeight,
        height_cm: profileData.heightCm,
        age: profileData.age,
        gender: profileData.gender,
        activity_level: profileData.activityLevel,
        target_calories: profileData.targetCalories,
        target_protein_g: profileData.targetProtein,
        target_carbs_g: profileData.targetCarbs,
        target_fat_g: profileData.targetFat,
        mission_start_date: profileData.missionStartDate,
        goals_setup_complete: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    // Insert goals
    if (goals.length > 0) {
      await supabase.from('forge_goals').insert(
        goals.map((g) => ({
          user_id: userId,
          goal_type: g.goalType,
          title: g.title,
          description: g.description || null,
          target_value: g.targetValue || null,
          target_unit: g.targetUnit || null,
          start_value: g.startValue || null,
          current_value: g.startValue || null,
          target_date: g.targetDate || null,
          display_order: g.displayOrder,
          is_active: true,
          is_achieved: false,
        }))
      );
    }
  },

  // ── Get active goals ──────────────────────────────────────
  async getActiveGoals(userId: string): Promise<ForgeGoal[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('forge_goals')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (error) return [];
      return (data || []).map(mapGoal);
    } catch {
      return [];
    }
  },

  // ── Mark goal as achieved and award badge ─────────────────
  async achieveGoal(userId: string, goalId: string, goalTitle: string): Promise<ForgeBadge | null> {
    const supabase = createClient();
    try {
      await supabase
        .from('forge_goals')
        .update({ is_achieved: true, is_active: false, achieved_at: new Date().toISOString() })
        .eq('id', goalId)
        .eq('user_id', userId);

      const { data: badge } = await supabase
        .from('forge_badges')
        .insert({
          user_id: userId,
          badge_type: 'goal_achieved',
          badge_key: `goal_${goalId}`,
          title: 'GOAL FORGED',
          description: `You achieved: ${goalTitle}`,
          icon: 'trophy',
          goal_id: goalId,
          is_seen: false,
        })
        .select()
        .single();

      return badge ? mapBadge(badge) : null;
    } catch {
      return null;
    }
  },

  // ── Get unseen badges ─────────────────────────────────────
  async getUnseenBadges(userId: string): Promise<ForgeBadge[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('forge_badges')
        .select('*')
        .eq('user_id', userId)
        .eq('is_seen', false)
        .order('earned_at', { ascending: false });
      if (error) return [];
      return (data || []).map(mapBadge);
    } catch {
      return [];
    }
  },

  // ── Mark badges as seen ───────────────────────────────────
  async markBadgesSeen(userId: string, badgeIds: string[]): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('forge_badges')
        .update({ is_seen: true })
        .in('id', badgeIds)
        .eq('user_id', userId);
    } catch {
      // non-blocking
    }
  },

  // ── Get all badges ────────────────────────────────────────
  async getAllBadges(userId: string): Promise<ForgeBadge[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('forge_badges')
        .select('*')
        .eq('user_id', userId)
        .order('earned_at', { ascending: false });
      if (error) return [];
      return (data || []).map(mapBadge);
    } catch {
      return [];
    }
  },

  // ── Get user profile ──────────────────────────────────────
  async getUserProfile(userId: string): Promise<any | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('forge_user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) return null;
      return data;
    } catch {
      return null;
    }
  },

  // ── Ensure profile exists ─────────────────────────────────
  async ensureProfile(userId: string, email: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('forge_user_profiles')
        .upsert({ id: userId, email }, { onConflict: 'id', ignoreDuplicates: true });
    } catch {
      // non-blocking
    }
  },

  // ── Favourite foods ───────────────────────────────────────
  async getFavouriteFoods(userId: string): Promise<ForgeFavouriteFood[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('forge_favourite_foods')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) return [];
      return (data || []).map(mapFavourite);
    } catch {
      return [];
    }
  },

  async addFavouriteFood(
    userId: string,
    food: {
      foodId: string;
      foodName: string;
      calPerServing: number;
      proteinPerServing: number;
      carbsPerServing: number;
      fatPerServing: number;
      servingSizeG: number;
      servingUnit: string;
    }
  ): Promise<ForgeFavouriteFood | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('forge_favourite_foods')
        .upsert(
          {
            user_id: userId,
            food_id: food.foodId,
            food_name: food.foodName,
            cal_per_serving: food.calPerServing,
            protein_per_serving: food.proteinPerServing,
            carbs_per_serving: food.carbsPerServing,
            fat_per_serving: food.fatPerServing,
            serving_size_g: food.servingSizeG,
            serving_unit: food.servingUnit,
          },
          { onConflict: 'user_id,food_id' }
        )
        .select()
        .single();
      if (error) return null;
      return mapFavourite(data);
    } catch {
      return null;
    }
  },

  async removeFavouriteFood(userId: string, favouriteId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('forge_favourite_foods')
        .delete()
        .eq('id', favouriteId)
        .eq('user_id', userId);
    } catch {
      // non-blocking
    }
  },
};
